import { Link } from "@inertiajs/react"
import { ChevronsUpDown, Plus, Wallet } from "lucide-react"
import { useEffect, useRef, useState, type MouseEvent } from "react"
import {
  COMPANY_TOP_UP_HREF,
  companyBalanceTitle,
  foreignBalancesShort,
  type CompanyBalanceShare,
} from "@/components/billing/company-balance-nav"
import {
  SpendLimitPopover,
  SpendLimitRailBadge,
  SpendLimitRingButton,
  SpendLimitRow,
  useSpendLimitTooltip,
} from "@/components/billing/spend-limit-nav"
import { CompanyLogo } from "@/components/people/company-logo"
import { CompanyMenu } from "@/components/shell/company-menu"
import { Badge } from "@/components/ui/primitives"
import { CollapsedNavPeekProvider, SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import { useRoleText } from "@/hooks/use-role-text"
import { useTranslation } from "@/hooks/use-translation"
import { formatRubWhole } from "@/lib/billing-format"
import { APP_NAME, displayAppName } from "@/lib/brand"
import { beginShellNav } from "@/lib/shell-nav-pending"
import { closeShotForShellNav } from "@/lib/shell-nav-shot"
import { cn } from "@/lib/utils"
import { useTaskNotificationsStore } from "@/stores/task-notifications-store"
import type { AuthShare } from "@/types/auth"
import type { SpendLimitShare } from "@/types/billing"

const PROJECTS_HREF = "/"

export type SidebarCompanyHeaderProps = {
  auth: AuthShare
  balance: CompanyBalanceShare | null
  spendLimit: SpendLimitShare | null
  collapsed: boolean
  railHovered: boolean
  onRailHoverChange: (hovered: boolean) => void
  onAction: () => void
}

export function SidebarCompanyHeader({
  auth,
  balance,
  spendLimit,
  collapsed,
  railHovered,
  onRailHoverChange,
  onAction,
}: SidebarCompanyHeaderProps) {
  const { t } = useTranslation("layout")
  const { t: billingT } = useTranslation("billing")
  const roleText = useRoleText()
  const [menuOpen, setMenuOpen] = useState(false)
  const [limitOpen, setLimitOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const logoRef = useRef<HTMLSpanElement>(null)
  const limitTooltip = useSpendLimitTooltip(spendLimit)
  const otherUnread = useTaskNotificationsStore((s) =>
    Object.values(s.summary?.companies ?? {}).some((count) => count > 0),
  )
  const company = auth.company

  // В свёрнутом сайдбаре шеврона нет — открытое меню закрываем, иначе оно повиснет без якоря.
  useEffect(() => {
    if (!collapsed) return
    setMenuOpen(false)
    setLimitOpen(false)
  }, [collapsed])

  const openProjects = (event: MouseEvent<Element>) => {
    onAction()
    if (event.defaultPrevented) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    if (event.currentTarget instanceof HTMLAnchorElement && event.currentTarget.target === "_blank") return
    // Логотип и название компании ведут в проекты; как пункты сайдбара, закрывают открытый шот.
    if (closeShotForShellNav(PROJECTS_HREF)) {
      event.preventDefault()
      return
    }
    beginShellNav(PROJECTS_HREF)
  }

  if (!company) {
    return (
      <Link
        href={PROJECTS_HREF}
        onClick={openProjects}
        className={cn("flex min-w-0 items-center gap-2.5", collapsed ? "justify-center" : "px-1.5")}
      >
        <CompanyLogo company={{ id: 0, name: APP_NAME }} size={36} brand />
        {!collapsed ? <span className="truncate text-sm font-semibold tracking-tight">{APP_NAME}</span> : null}
      </Link>
    )
  }

  const name = displayAppName(company)
  const logoCompany = { id: company.id, name, slug: company.slug }
  const archived = company.archived === true
  const superAdminLine = auth.user?.super_admin === true
  const roleLabel = company.role ? roleText.label("company", company.role) : null
  const balanceTitle = balance ? companyBalanceTitle(billingT, balance) : null
  const foreignShort = balance ? foreignBalancesShort(balance) : null
  const menuLabel = `${name} · ${t("shell.companyMenu.open")}`
  const projectsLabel = `${name} · ${t("shell.root.projects")}`
  const toggleMenu = () => {
    setLimitOpen(false)
    setMenuOpen((value) => !value)
  }
  const toggleLimit = () => {
    setMenuOpen(false)
    setLimitOpen((value) => !value)
  }

  if (collapsed) {
    const tooltip = archived
      ? t("shell.companyHeader.archivedTooltip", { company: name })
      : roleLabel
        ? t("shell.companyHeader.railTooltip", { company: name, role: roleLabel })
        : name
    const details = [balanceTitle, limitTooltip].filter((line): line is string => line != null)
    const dot =
      balance && balance.available_microcents < 0
        ? "danger"
        : auth.incoming_invitations.length > 0 || otherUnread
          ? "accent"
          : null

    // Шапка рельса ростом с карточку (h-12), поэтому иконки пунктов при сворачивании остаются на месте.
    return (
      <div
        ref={rootRef}
        className="flex h-12 items-center justify-center"
        onMouseEnter={() => onRailHoverChange(true)}
        onMouseLeave={() => onRailHoverChange(false)}
      >
        <CollapsedNavPeekProvider enabled={!limitOpen} railHovered={railHovered}>
          <SidebarTooltip label={tooltip} details={details} open={limitOpen ? false : undefined}>
            <span ref={logoRef} className="relative flex">
              <Link
                href={PROJECTS_HREF}
                aria-label={projectsLabel}
                onClick={openProjects}
                className="rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
              >
                <CompanyLogo company={logoCompany} size={36} dot={dot} brand />
              </Link>
              {spendLimit ? <SpendLimitRailBadge share={spendLimit} open={limitOpen} onToggle={toggleLimit} /> : null}
            </span>
          </SidebarTooltip>
        </CollapsedNavPeekProvider>
        {spendLimit ? (
          <SpendLimitPopover
            share={spendLimit}
            open={limitOpen}
            onClose={() => setLimitOpen(false)}
            anchorRef={logoRef}
            rootRef={rootRef}
            direction="right"
            onNavigate={onAction}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div ref={rootRef}>
      <div
        className={cn(
          "group relative h-12 rounded-lg transition-colors hover:bg-[var(--ui-nav-idle-hover-bg)]",
          menuOpen && "bg-[var(--ui-nav-idle-hover-bg)]",
        )}
      >
        <Link
          href={PROJECTS_HREF}
          aria-label={projectsLabel}
          title={balanceTitle ? `${name} · ${balanceTitle}` : name}
          onClick={openProjects}
          className="absolute inset-0 rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
        />
        <div className="pointer-events-none relative flex h-full items-center gap-2.5 px-1.5">
          <CompanyLogo company={logoCompany} size={36} brand />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-[13px] leading-[18px] font-semibold text-ui-text-primary">{name}</span>
              {archived ? (
                <Badge tone="amber" className="shrink-0 px-1.5 py-0 text-[10px] leading-4">
                  {t("shell.companyHeader.archivedBadge")}
                </Badge>
              ) : null}
            </div>
            {balance ? (
              <div className="flex h-5 items-center gap-1 text-[11px] text-ui-text-muted">
                <Wallet className="h-3 w-3 shrink-0" />
                <span
                  className={cn(
                    "min-w-0 truncate text-[var(--ui-nav-idle-fg)] tabular-nums",
                    balance.available_microcents < 0 && "text-ui-status-failed light:text-ui-danger",
                  )}
                >
                  {formatRubWhole(balance.available_microcents)}
                  {foreignShort ? <span className="text-ui-text-muted"> · {foreignShort}</span> : null}
                </span>
                {spendLimit ? (
                  <SpendLimitRingButton share={spendLimit} open={limitOpen} onToggle={toggleLimit} />
                ) : null}
                {balance.can_top_up ? (
                  <Link
                    href={COMPANY_TOP_UP_HREF}
                    aria-label={billingT("client.topUp")}
                    title={billingT("client.topUp")}
                    onClick={onAction}
                    className="pointer-events-auto flex size-5 shrink-0 items-center justify-center rounded-md text-ui-text-muted transition-colors hover:bg-ui-accent hover:text-ui-on-accent focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                  >
                    <Plus className="h-3 w-3" />
                  </Link>
                ) : null}
              </div>
            ) : spendLimit ? (
              <div className="flex h-5 items-center text-[11px] text-ui-text-muted">
                <SpendLimitRow share={spendLimit} open={limitOpen} onToggle={toggleLimit} />
              </div>
            ) : superAdminLine ? (
              <div className="truncate text-[11px] leading-5 text-ui-text-muted">
                {t("shell.companyHeader.superAdmin")}
              </div>
            ) : null}
          </div>
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            aria-label={menuLabel}
            title={t("shell.companyMenu.open")}
            onClick={toggleMenu}
            className={cn(
              "pointer-events-auto flex size-7 shrink-0 items-center justify-center rounded-lg text-ui-text-muted transition-colors",
              "hover:bg-ui-surface-hover hover:text-ui-text-primary focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none",
              menuOpen && "bg-ui-surface-hover text-ui-text-primary",
            )}
          >
            <ChevronsUpDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <CompanyMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        returnFocusRef={triggerRef}
        auth={auth}
        onAction={onAction}
      />
      {spendLimit ? (
        <SpendLimitPopover
          share={spendLimit}
          open={limitOpen}
          onClose={() => setLimitOpen(false)}
          anchorRef={rootRef}
          rootRef={rootRef}
          onNavigate={onAction}
        />
      ) : null}
    </div>
  )
}
